import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, addDoc, serverTimestamp, doc, getDoc, increment, getDocs, where, runTransaction } from 'firebase/firestore';
import { printSalesInvoice } from './printInvoice';
import { codFeeOf, formatFee } from './dueUtil';

// একটা খালি ইনভয়েস ট্যাবের শুরুর অবস্থা। deliveryPaidBy: 'customer' মানে ডেলিভারি চার্জ
// কাস্টমারের বিলে যোগ হবে; 'shop' মানে দোকান নিজে দিচ্ছে, তাই বিলে যোগ হবে না — খরচ হবে।
const emptyTab = () => ({
  cart: [],
  customer: { name: '', phone: '', phone2: '', address: '' },
  discountType: 'tk',
  discount: 0,
  deliveryCharge: 0,
  deliveryPaidBy: 'customer',
  deliveryMethod: 'direct', // 'direct' (walk-in, hidden from Courier Sync) or 'courier' (needs booking)
  deliveryZoneType: 'inside', // 'inside' | 'suburb' | 'outside' -- Pathao's three price tiers
  itemWeight: 0.5,
  paid: '',
  paidMethod: 'Cash' // কাস্টমার কীভাবে টাকা দিলো -- Cash/Bank/bKash, Accounts পেজের ব্রেকডাউনে ব্যবহার হয়
});

const ZONE_LABELS = {
  inside: 'Inside Dhaka',
  suburb: 'Suburbs',
  outside: 'Outside Dhaka'
};

// Pathao-র প্রকাশিত রেট কার্ড (Sohan-এর অ্যাকাউন্টের pricing plan -- API-র দেওয়া সংখ্যার সাথে
// হুবহু মিলিয়ে দেখা হয়েছিল)। Pathao-র সার্ভার সাড়া না দিলে এটা দেখানো হয়, যাতে ইনভয়েস
// লেখার সময় ডেলিভারি ফি-র ঘরটা ফাঁকা না থাকে। (2026-10-08-এ Pathao-র sandbox সার্ভার
// 522 দিচ্ছিল, তাই চিপটা পুরো উধাও হয়ে গিয়েছিল।)
const PATHAO_RATE_CARD = {
  inside: { 0.5: 60, 1: 70, 1.5: 90, 2: 90 },
  suburb: { 0.5: 80, 1: 100, 1.5: 130, 2: 130 },
  outside: { 0.5: 110, 1: 130, 1.5: 170, 2: 170 }
};

const Sales = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTabId, setActiveTabId] = useState(1);

  const [tabs, setTabs] = useState({ 1: emptyTab() });
  
  const [loading, setLoading] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  // "Advance" ঘরে টাকা লেখার পর যদি "কে ডেলিভারি দিবে" বাটন পাল্টানো হয়, টোটাল বিল
  // বদলে যায় কিন্তু Advance-এর ঘর নিজে থেকে বদলায় না -- ফলে একটা মিথ্যা Due/Return
  // দেখা যেতে পারে যেটা আসলে ভুলে হয়েছে, সত্যিকারের ফেরত/বাকি না। সংখ্যাটা ঠিকই থাকে
  // (Sohan নিজেই একটা টেস্ট সেলে দেখেছেন), শুধু স্টাফকে মনে করিয়ে দেওয়া হচ্ছে যাতে
  // Advance-এর ঘরটা আবার চেক করে নেয়।
  const [deliveryPayerWarn, setDeliveryPayerWarn] = useState(false);
  const [pathaoFees, setPathaoFees] = useState(null); // { rates: {inside|suburb|outside: {weight: fee}}, weights, env }
  const feeFetchRef = useRef({ loading: false, lastTry: 0 });

  // ---- ডাবল-সাবমিট/ডাবল-ক্লিক প্রতিরোধ করার জন্য একটা lock ----
  // useRef ব্যবহার করা হয়েছে কারণ এটা state-এর মতো re-render এর জন্য
  // অপেক্ষা করে না — ক্লিক করা মাত্রই সাথে সাথে lock হয়ে যায়, তাই
  // দ্রুত দুইবার ক্লিক করলেও দ্বিতীয় কলটা সাথে সাথেই আটকে যাবে।
  const isSubmittingRef = useRef(false);

  const currentTab = tabs[activeTabId];
  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

  const getFormattedDateTime = () => {
    const d = new Date();
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).replace(/ /g, '-');
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  };

  useEffect(() => {
    const q = query(collection(db, "products"));
    return onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(p => (p.stock || 0) > 0));
    });
  }, []);

  // অন্য ট্যাবে গেলে আগের ট্যাবের সতর্কতা যেন নতুন ট্যাবে টেনে না আসে
  useEffect(() => {
    setDeliveryPayerWarn(false);
  }, [activeTabId]);

  // What Pathao will charge for a courier delivery, so the delivery charge on the
  // invoice can be set with that in mind. A failure never blocks a sale -- the chip
  // falls back to PATHAO_RATE_CARD -- and is retried the next time Courier Delivery
  // is picked (at most once a minute), so a Pathao outage doesn't need a page reload.
  const isCourierTab = currentTab.deliveryMethod === 'courier';
  useEffect(() => {
    const f = feeFetchRef.current;
    if (pathaoFees || f.loading || Date.now() - f.lastTry < 60000) return;
    if (f.lastTry && !isCourierTab) return; // first load always; retries only when needed
    f.loading = true;
    f.lastTry = Date.now();
    getDoc(doc(db, 'settings', 'websiteSync')).then((snap) => {
      const secret = snap.exists() ? snap.data().secret : '';
      if (!secret) return;
      return fetch('/api/courier/pathao/fee-guide', { headers: { 'X-App-Secret': secret } })
        .then((r) => r.json())
        .then((d) => { if (d && d.rates) setPathaoFees(d); });
    }).catch(() => {}).finally(() => { f.loading = false; });
  }, [isCourierTab, pathaoFees]);

  const updateTabData = (newData) => {
    setTabs(prev => ({ ...prev, [activeTabId]: { ...prev[activeTabId], ...newData } }));
  };

  const addNewTab = () => {
    const tabKeys = Object.keys(tabs).map(Number);
    const nextTabId = Math.max(...tabKeys) + 1;
    if (tabKeys.length >= 10) return alert("Maximum 10 tabs can be opened!");
    
    setTabs(prev => ({ ...prev, [nextTabId]: emptyTab() }));
    setActiveTabId(nextTabId);
  };

  const closeTab = (id, e) => {
    e.stopPropagation();
    const tabKeys = Object.keys(tabs);
    if (tabKeys.length === 1) return alert("At least one tab must remain open!");
    
    const newTabs = { ...tabs };
    delete newTabs[id];
    setTabs(newTabs);
    if (activeTabId === id) setActiveTabId(Number(Object.keys(newTabs)[0]));
  };

  const handleCustomerSearch = async (e) => {
    if (e.key === 'Enter' && currentTab.customer.phone) {
      const q = query(collection(db, "customers"), where("phone", "==", currentTab.customer.phone));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = snap.docs[0].data();
        updateTabData({ customer: { ...currentTab.customer, name: data.name, address: data.address || '', phone2: data.phone2 || currentTab.customer.phone2 || '' } });
      }
    }
  };

  const addToCart = (product) => {
    const exists = currentTab.cart.find(item => item.id === product.id);
    if (exists) {
      if (exists.qty >= product.stock) return alert("Stock out!");
      const newCart = currentTab.cart.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      updateTabData({ cart: newCart });
    } else {
      updateTabData({ cart: [...currentTab.cart, { ...product, qty: 1, itemDiscountType: 'tk', itemDiscountValue: 0 }] });
    }
  };

  const updateCartItem = (id, field, value) => {
    const newCart = currentTab.cart.map(item => {
      if (item.id === id) {
        let val = value;
        if (field === 'itemDiscountValue') {
          val = Math.max(0, Number(value) || 0);
        }
        return { ...item, [field]: val };
      }
      return item;
    });
    updateTabData({ cart: newCart });
  };

  const removeCartItem = (id) => {
    const newCart = currentTab.cart.filter(item => item.id !== id);
    updateTabData({ cart: newCart });
  };

  const calculateItemDiscountAmount = (item) => {
    const baseTotal = item.price * item.qty;
    const discVal = Math.max(0, Number(item.itemDiscountValue) || 0);
    if (item.itemDiscountType === 'percent') {
      return Math.round((baseTotal * discVal) / 100);
    } else {
      return Math.round(discVal);
    }
  };

  const calculateItemOriginalTotal = (item) => {
    return Math.round(item.price * item.qty);
  };

  const totalItemDiscounts = currentTab.cart.reduce((sum, item) => sum + calculateItemDiscountAmount(item), 0);
  const subtotal = currentTab.cart.reduce((total, item) => total + calculateItemOriginalTotal(item), 0);
  
  let overallDiscountAmount = 0;
  const numDiscount = Math.max(0, Number(currentTab.discount) || 0);
  if (currentTab.discountType === 'percent') {
    overallDiscountAmount = Math.round(subtotal * (numDiscount / 100));
  } else {
    overallDiscountAmount = Math.round(numDiscount);
  }

  const totalDiscountCombined = totalItemDiscounts + overallDiscountAmount;
  const deliveryChargeVal = Math.max(0, Number(currentTab.deliveryCharge) || 0);
  // দোকান ডেলিভারি চার্জ দিলে সেটা কাস্টমারের বিলে যোগ হয় না — ওটা দোকানের খরচ
  const customerPaysDelivery = currentTab.deliveryPaidBy !== 'shop';
  const billedDelivery = customerPaysDelivery ? deliveryChargeVal : 0;
  const totalBill = Math.max(0, Math.round(subtotal - totalDiscountCombined + billedDelivery));
  const paidVal = Number(currentTab.paid) || 0;

  // ✅ FIX: Due এবং Return আলাদাভাবে হিসাব করা হচ্ছে
  const dueAmount = paidVal < totalBill ? Math.round(totalBill - paidVal) : 0;
  const returnAmount = paidVal > totalBill ? Math.round(paidVal - totalBill) : 0;

  // কুরিয়ার সেলে বাকি টাকাটা কাস্টমারের বাকি নয় — ওটা COD, কুরিয়ার কালেক্ট করে
  // এনে দিবে। তাই এখানে "Paid" ঘরটা শুধু অগ্রিম (bKash/নগদে আগে নেওয়া টাকা) বোঝায়,
  // আর বাকিটা COD হিসেবে দেখানো হয়। বিস্তারিত src/dueUtil.js-এ।
  const isCourierSale = currentTab.deliveryMethod === 'courier';
  const codAmount = isCourierSale ? dueAmount : 0;

  // What Pathao will bill for this destination + weight, so the invoice's delivery
  // charge can be set against it while the customer is still on the phone.
  const livePathaoFee = pathaoFees?.rates?.[currentTab.deliveryZoneType]?.[currentTab.itemWeight] ?? null;
  const pathaoMinCharge = livePathaoFee ?? PATHAO_RATE_CARD[currentTab.deliveryZoneType]?.[currentTab.itemWeight] ?? null;
  const underchargingDelivery = currentTab.deliveryMethod === 'courier'
    && customerPaysDelivery
    && pathaoMinCharge != null
    && deliveryChargeVal < pathaoMinCharge;

  const showInvoicePreview = () => {
    if (currentTab.cart.length === 0) return alert("Cart is empty!");
    // কুরিয়ার সেলে হাতে কোনো টাকা নেওয়াই হয়নি (পুরোটা COD), তাই Paid ঘর খালি থাকাটাই
    // স্বাভাবিক — ০ ধরে নেওয়া হয়। ডাইরেক্ট সেলে খালি মানে বসাতে ভুলে গেছে, তাই আটকাবে।
    if (!isCourierSale && (currentTab.paid === "" || currentTab.paid === null || isNaN(currentTab.paid))) {
      return alert("Please enter the paid amount!");
    }

    setPreviewInvoice({
      invoiceId: "INV-" + Date.now().toString(36).toUpperCase(),
      ...currentTab.customer,
      soldBy: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff',
      items: currentTab.cart.map(item => ({
        ...item,
        finalItemTotal: calculateItemOriginalTotal(item),
        itemDiscAmount: calculateItemDiscountAmount(item),
        itemDiscountText: item.itemDiscountType === 'percent' ? `${item.itemDiscountValue}%` : `৳ ${formatTk(item.itemDiscountValue)}`
      })),
      subtotal: Math.round(subtotal),
      itemDiscountTotal: totalItemDiscounts,
      overallDiscountType: currentTab.discountType,
      overallDiscountVal: currentTab.discount,
      overallDiscountAmount: overallDiscountAmount,
      totalDiscount: totalDiscountCombined,
      deliveryCharge: Math.round(deliveryChargeVal),
      deliveryMethod: currentTab.deliveryMethod === 'courier' ? 'courier' : 'direct',
      deliveryZoneType: currentTab.deliveryZoneType || 'inside',
      itemWeight: Number(currentTab.itemWeight) || 0.5,
      deliveryPaidBy: customerPaysDelivery ? 'customer' : 'shop',
      deliveryPaidByName: customerPaysDelivery
        ? (currentTab.customer.name || 'Customer')
        : (auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Kizuna'),
      totalBill: Math.round(totalBill),
      paid: Math.round(paidVal),
      paidMethod: currentTab.paidMethod || 'Cash',
      dueAmount: Math.round(dueAmount),       // ✅ FIX: Firestore-এ due সেভ হবে
      returnAmount: Math.round(returnAmount),
      dateTime: getFormattedDateTime()
    });
  };

  const handleFinalProcess = async (shouldPrint) => {
    // ---- ডাবল-ক্লিক / ডাবল-সাবমিট গার্ড ----
    // যদি আগের রিকোয়েস্ট এখনও প্রসেস হচ্ছে, তাহলে নতুন কোনো ক্লিক
    // একদমই গ্রহণ করা হবে না — এটাই মূল ফিক্স যা ডুপ্লিকেট সেল এন্ট্রি
    // তৈরি হওয়া বন্ধ করবে।
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setLoading(true);
    try {
      if (currentTab.customer.phone || currentTab.customer.phone2) {
        const searchPhone = currentTab.customer.phone || currentTab.customer.phone2;
        const custSnap = await getDocs(query(collection(db, "customers"), where("phone", "==", searchPhone)));
        if (custSnap.empty) await addDoc(collection(db, "customers"), { ...currentTab.customer, createdAt: serverTimestamp() });
      }
      // ---- সেল সেভ + স্টক কমানো একটাই Firestore transaction-এ, invoiceId-কে ডকুমেন্ট আইডি হিসেবে ব্যবহার করে ----
      // এতে করে একই সেল দুইবার সাবমিট হলেও (নেটওয়ার্ক রিট্রাই, ডাবল-ক্লিক ইত্যাদি)
      // দ্বিতীয়বার শুধু "already exists" পেয়ে থেমে যাবে — নতুন করে সেল বা স্টক ডাবল হবে না।
      await runTransaction(db, async (transaction) => {
        const saleRef = doc(db, "sales", previewInvoice.invoiceId);
        const saleSnap = await transaction.get(saleRef);
        if (saleSnap.exists()) return;
        transaction.set(saleRef, { ...previewInvoice, createdAt: serverTimestamp() });
        currentTab.cart.forEach(item => {
          transaction.update(doc(db, "products", item.id), { stock: increment(-item.qty) });
          // লাইফটাইম বিক্রির কাউন্টার — এটা দিয়ে Home ড্যাশবোর্ড "Top Selling Products"
          // বের করে, পুরো sales history আবার পড়ার দরকার হয় না।
          transaction.set(doc(db, "productStats", item.id), {
            name: item.name,
            qtySold: increment(item.qty)
          }, { merge: true });
        });
      });

      if (shouldPrint && previewInvoice) {
        printSalesInvoice(previewInvoice);
      }

      setPreviewInvoice(null);
      updateTabData(emptyTab());
      setDeliveryPayerWarn(false);

    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
      // ---- সাবমিট শেষ হওয়ার পর lock খুলে দেওয়া হচ্ছে, যাতে পরের সেল করা যায় ----
      isSubmittingRef.current = false;
    }
  };

  return (
    <div style={{ display: 'flex', gap: '20px', height: '82vh' }}>
      <div className="white-box" style={{ flex: 1, overflowY: 'auto' }}>
        <input 
          type="text" 
          placeholder="🔍 Search Saree..." 
          className="input-field" 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} 
        />
        {products
          .filter(p => p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(p => (
            <div key={p.id} className="hover-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f9f9f9' }}>
              <div>
                <b>{p.name}</b>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  <span style={{ background: '#f0f0ff', padding: '2px 6px', borderRadius: '4px', color: '#4e43e7', fontWeight: 'bold' }}>
                    {p.category || 'General'}
                  </span> 
                  {' | '} Stk: {p.stock} | ৳ {formatTk(p.price)}
                </div>
              </div>
              <button onClick={() => addToCart(p)} style={{ background: '#4e43e7', color: '#fff', border: 'none', borderRadius: '10px', padding: '8px 15px', cursor: 'pointer' }}>+</button>
            </div>
          ))}
      </div>

      <div className="white-box" style={{ flex: 1.5, display: 'flex', flexDirection: 'column', padding: '15px' }}>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '8px', overflowX: 'auto', paddingBottom: '2px', flexShrink: 0 }}>
          {Object.keys(tabs).map(id => (
            <div key={id} style={{ position: 'relative' }}>
              <button 
                onClick={() => setActiveTabId(Number(id))} 
                style={{ 
                  padding: '6px 18px', borderRadius: '8px 8px 0 0', border: 'none', 
                  background: activeTabId === Number(id) ? '#4e43e7' : '#eee', 
                  color: activeTabId === Number(id) ? '#fff' : '#333', 
                  cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px',
                  whiteSpace: 'nowrap', fontSize: '13px'
                }}
              >
                INV {id} {tabs[id].cart.length > 0 && <span style={{width:'6px', height:'6px', background:'#00ff00', borderRadius:'50%'}}></span>}
                {Object.keys(tabs).length > 1 && (
                   <span onClick={(e) => closeTab(Number(id), e)} style={{ marginLeft: '6px', color: activeTabId === Number(id) ? '#fff' : '#999', fontSize: '13px' }}>×</span>
                )}
              </button>
            </div>
          ))}
          
          <button 
            onClick={addNewTab} 
            style={{ 
              padding: '6px 12px', borderRadius: '8px', border: '2px dashed #ccc', 
              background: 'transparent', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' 
            }}
          > + </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
          <input placeholder="Phone 1 (Enter)" value={currentTab.customer.phone} onKeyDown={handleCustomerSearch} onChange={e => updateTabData({ customer: { ...currentTab.customer, phone: e.target.value } })} className="input-field" style={{ flex: 1, padding: '6px 10px', fontSize: '13px' }} />
          <input placeholder="Phone 2 (Alt / Optional)" value={currentTab.customer.phone2 || ''} onChange={e => updateTabData({ customer: { ...currentTab.customer, phone2: e.target.value } })} className="input-field" style={{ flex: 1, padding: '6px 10px', fontSize: '13px' }} />
          <input placeholder="Customer Name" value={currentTab.customer.name} onChange={e => updateTabData({ customer: { ...currentTab.customer, name: e.target.value } })} className="input-field" style={{ flex: 1, padding: '6px 10px', fontSize: '13px' }} />
        </div>
        <input placeholder="Address" value={currentTab.customer.address} onChange={e => updateTabData({ customer: { ...currentTab.customer, address: e.target.value } })} className="input-field" style={{ marginBottom: '8px', padding: '6px 10px', fontSize: '13px' }} />

        {/* Flexes instead of a fixed height so the cart gives up space when the rows
            below it grow (courier fee guide, warnings) rather than pushing the invoice
            tabs off the top of a fixed-height panel. */}
        <div style={{ flex: 1, minHeight: '200px', overflowY: 'auto', border: '1px solid #f4f7fe', borderRadius: '12px', marginBottom: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f4f7fe', position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th style={{padding:'8px 10px', textAlign:'left', fontSize:'13px'}}>Item</th>
                <th style={{fontSize:'13px'}}>Qty</th>
                <th style={{fontSize:'13px'}}>Item Disc</th>
                <th style={{fontSize:'13px'}}>Total</th>
                <th style={{fontSize:'13px'}}>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentTab.cart.map(item => {
                const discAmt = calculateItemDiscountAmount(item);
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{padding:'8px 10px'}}>
                      <b style={{ fontSize: '13px' }}>{item.name}</b>
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '1px' }}>
                        <span style={{ background: '#f0f0ff', padding: '1px 4px', borderRadius: '3px', color: '#4e43e7', fontWeight: 'bold' }}>
                          {item.category || 'General'}
                        </span>
                      </div>
                    </td>
                    <td align="center">
                      <input 
                        type="number" 
                        value={item.qty} 
                        onChange={e => updateCartItem(item.id, 'qty', Math.max(1, Number(e.target.value)))}
                        style={{ width: '40px', textAlign: 'center', padding: '3px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '12px' }} 
                      />
                    </td>
                    <td align="center">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
                          <select 
                            value={item.itemDiscountType} 
                            onChange={e => updateCartItem(item.id, 'itemDiscountType', e.target.value)}
                            style={{ padding: '2px', borderRadius: '3px', border: '1px solid #ccc', fontSize: '11px' }}
                          >
                            <option value="tk">৳</option>
                            <option value="percent">%</option>
                          </select>
                          <input 
                            type="number" 
                            min="0"
                            value={item.itemDiscountValue} 
                            onFocus={e => e.target.select()}
                            onChange={e => updateCartItem(item.id, 'itemDiscountValue', e.target.value)}
                            placeholder="0"
                            style={{ width: '45px', padding: '2px', borderRadius: '3px', border: '1px solid #ccc', textAlign: 'center', fontSize: '12px' }} 
                          />
                        </div>
                        {discAmt > 0 && (
                          <span style={{ fontSize: '10px', color: '#e74c3c', fontWeight: 'bold' }}>
                            (-৳ {formatTk(discAmt)})
                          </span>
                        )}
                      </div>
                    </td>
                    <td align="right" style={{ paddingRight: '10px', fontWeight: 'bold', fontSize: '13px' }}>
                      ৳ {formatTk(calculateItemOriginalTotal(item))}
                    </td>
                    <td align="center">
                      <button 
                        onClick={() => removeCartItem(item.id)}
                        style={{ background: '#ffeded', color: 'red', border: 'none', padding: '4px 8px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                        title="Remove Item"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ background: '#f8f9ff', padding: '10px 15px', borderRadius: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>Overall Discount:</span>
              <select 
                value={currentTab.discountType} 
                onChange={e => updateTabData({ discountType: e.target.value })}
                style={{ padding: '4px', borderRadius: '5px', border: '1px solid #ccc', background: '#fff', fontSize: '12px' }}
              >
                <option value="tk">৳</option>
                <option value="percent">%</option>
              </select>
              <input 
                type="number" 
                min="0"
                value={currentTab.discount} 
                onFocus={(e) => e.target.select()}
                onChange={e => updateTabData({ discount: Math.max(0, Number(e.target.value)) })} 
                placeholder="0"
                style={{ width: '60px', padding: '4px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '12px' }} 
              />
              {totalDiscountCombined > 0 && (
                <span style={{ fontSize: '12px', color: '#e74c3c', fontWeight: 'bold', background: '#ffebee', padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                  Total Disc: ৳ {formatTk(totalDiscountCombined)}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#888' }}>Sale Type:</span>
              <button
                type="button"
                onClick={() => updateTabData({ deliveryMethod: 'direct' })}
                style={{
                  padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                  border: `1px solid ${currentTab.deliveryMethod !== 'courier' ? '#4a00e0' : '#ddd'}`,
                  background: currentTab.deliveryMethod !== 'courier' ? '#f4f0ff' : '#fff',
                  color: currentTab.deliveryMethod !== 'courier' ? '#4a00e0' : '#999'
                }}
              >
                Direct Sale
              </button>
              <button
                type="button"
                onClick={() => updateTabData({ deliveryMethod: 'courier' })}
                style={{
                  padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                  border: `1px solid ${currentTab.deliveryMethod === 'courier' ? '#4a00e0' : '#ddd'}`,
                  background: currentTab.deliveryMethod === 'courier' ? '#f4f0ff' : '#fff',
                  color: currentTab.deliveryMethod === 'courier' ? '#4a00e0' : '#999'
                }}
              >
                Courier Delivery
              </button>
              {currentTab.deliveryMethod === 'courier' && (!currentTab.customer.phone || !currentTab.customer.address) && (
                <span style={{ fontSize: '11px', color: '#c0392b' }}>Add phone &amp; address above for booking later</span>
              )}
            </div>

            {currentTab.deliveryMethod === 'courier' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', fontSize: '11px' }}>
                {Object.keys(ZONE_LABELS).map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => updateTabData({ deliveryZoneType: z })}
                    title={z === 'suburb' ? 'Gazipur, Keraniganj, Narayanganj, Savar' : undefined}
                    style={{
                      padding: '3px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                      border: `1px solid ${currentTab.deliveryZoneType === z ? '#4a00e0' : '#ddd'}`,
                      background: currentTab.deliveryZoneType === z ? '#4a00e0' : '#fff',
                      color: currentTab.deliveryZoneType === z ? '#fff' : '#666'
                    }}
                  >
                    {ZONE_LABELS[z]}
                  </button>
                ))}
                <select
                  value={currentTab.itemWeight}
                  onChange={(e) => updateTabData({ itemWeight: Number(e.target.value) })}
                  style={{ padding: '3px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '11px' }}
                >
                  {[0.5, 1, 1.5, 2].map((w) => <option key={w} value={w}>{w} kg</option>)}
                </select>
                {pathaoMinCharge != null && (
                  <span
                    title={livePathaoFee == null
                      ? "Pathao's server isn't responding right now, so this is from Pathao's standard rate card rather than a live quote."
                      : undefined}
                    style={{
                      padding: '3px 8px', borderRadius: '5px', fontWeight: 'bold',
                      background: underchargingDelivery ? '#ffebee' : '#eafff3',
                      color: underchargingDelivery ? '#c0392b' : '#218c61'
                    }}
                  >
                    Pathao charges ৳ {formatTk(pathaoMinCharge)}
                    {livePathaoFee == null && ' (rate card)'}
                    {underchargingDelivery && ` — you'd lose ৳ ${formatTk(pathaoMinCharge - deliveryChargeVal)}`}
                  </span>
                )}
                {/* Pathao COD টাকার ১% আলাদা কেটে নেয় (ডেলিভারি ফি-র বাইরে) -- সেটা দোকানের খরচ, কাস্টমারের না */}
                {codAmount > 0 && (
                  <span
                    title="Pathao also deducts 1% of the COD amount from your payout. The customer isn't charged this — it's your cost."
                    style={{ padding: '3px 8px', borderRadius: '5px', fontWeight: 'bold', background: '#fff3cd', color: '#8a6d3b' }}
                  >
                    + COD fee ৳ {formatFee(codFeeOf(codAmount))} (1%)
                    {pathaoMinCharge != null && ` = ৳ ${formatFee(pathaoMinCharge + codFeeOf(codAmount))} to Pathao`}
                  </span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>Delivery Charge:</span>
                <input
                  type="number"
                  min="0"
                  value={currentTab.deliveryCharge}
                  onFocus={(e) => e.target.select()}
                  onChange={e => updateTabData({ deliveryCharge: Math.max(0, Number(e.target.value)) })}
                  placeholder="0"
                  style={{
                    width: '70px', padding: '4px', borderRadius: '5px', fontSize: '12px',
                    border: underchargingDelivery ? '2px solid #e74c3c' : '1px solid #ccc',
                    background: underchargingDelivery ? '#ffebee' : '#fff'
                  }}
                />
                {deliveryChargeVal > 0 && (
                  customerPaysDelivery ? (
                    <span style={{ fontSize: '12px', color: '#218c61', fontWeight: 'bold', background: '#eafff3', padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                      +৳ {formatTk(deliveryChargeVal)} on bill
                    </span>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#c0392b', fontWeight: 'bold', background: '#ffebee', padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                      ৳ {formatTk(deliveryChargeVal)} shop expense
                    </span>
                  )
                )}
              </div>
              {/* কে ডেলিভারি চার্জ দিচ্ছে — সবুজ: কাস্টমার (বিলে যোগ হবে), লাল: দোকান (খরচ হবে) */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <span style={{ fontSize: '11px', color: '#888', alignSelf: 'center' }}>Paid by:</span>
                <button
                  type="button"
                  onClick={() => {
                    // দেলিভারি-দাতা পাল্টানোয় টোটাল বিল বদলে যায়, Advance-এর ঘর বদলায় না --
                    // Advance-এ আগে থেকে কিছু লেখা থাকলে সেটা এখন আর ঠিক নাও থাকতে পারে
                    if (currentTab.deliveryPaidBy !== 'customer' && Number(currentTab.paid) > 0) setDeliveryPayerWarn(true);
                    updateTabData({ deliveryPaidBy: 'customer' });
                  }}
                  style={{
                    padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                    border: `1px solid ${customerPaysDelivery ? '#10b981' : '#ddd'}`,
                    background: customerPaysDelivery ? '#eafff3' : '#fff',
                    color: customerPaysDelivery ? '#218c61' : '#999'
                  }}
                >
                  Customer
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (currentTab.deliveryPaidBy !== 'shop' && Number(currentTab.paid) > 0) setDeliveryPayerWarn(true);
                    updateTabData({ deliveryPaidBy: 'shop' });
                  }}
                  style={{
                    padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                    border: `1px solid ${!customerPaysDelivery ? '#e74c3c' : '#ddd'}`,
                    background: !customerPaysDelivery ? '#ffebee' : '#fff',
                    color: !customerPaysDelivery ? '#c0392b' : '#999'
                  }}
                >
                  Kizuna
                </button>
              </div>
            </div>

            {deliveryPayerWarn && (
              <div style={{ fontSize: '11px', color: '#8a6d3b', background: '#fff3cd', padding: '6px 10px', borderRadius: '6px' }}>
                ⚠️ You just changed who pays delivery — the {isCourierSale ? 'Advance' : 'Paid'} amount above wasn't updated automatically. Please recheck it before confirming.
              </div>
            )}

            {/* কুরিয়ার সেলে এই ঘরটা "অগ্রিম" — খালি রাখলে পুরো বিলটাই COD হয়ে যাবে */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px' }}>{isCourierSale ? 'Advance:' : 'Paid:'}</span>
              <input
                type="number"
                value={currentTab.paid}
                onFocus={(e) => e.target.select()}
                onChange={e => { updateTabData({ paid: e.target.value }); setDeliveryPayerWarn(false); }}
                placeholder="0"
                style={{ width: '120px', fontWeight: 'bold', padding: '4px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '12px' }}
              />
              {/* কাস্টমার এই টাকাটা ক্যাশে, ব্যাংকে নাকি বিকাশে দিলো -- Accounts পেজের
                  Cash/Bank/bKash ব্রেকডাউনে এই মেথড অনুযায়ীই যোগ হবে */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {['Cash', 'Bank', 'bKash'].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => updateTabData({ paidMethod: m })}
                    style={{
                      padding: '4px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold',
                      border: `1px solid ${(currentTab.paidMethod || 'Cash') === m ? '#4e43e7' : '#ddd'}`,
                      background: (currentTab.paidMethod || 'Cash') === m ? '#f0f0ff' : '#fff',
                      color: (currentTab.paidMethod || 'Cash') === m ? '#4e43e7' : '#999'
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
              {isCourierSale && (
                <span
                  title="এই টাকাটা কুরিয়ার ডেলিভারির সময় কাস্টমারের কাছ থেকে নিবে, পরে Pathao আপনাকে পাঠাবে"
                  style={{ fontSize: '12px', fontWeight: 'bold', color: '#b45309', background: '#fff7e6', padding: '2px 8px', borderRadius: '5px', whiteSpace: 'nowrap' }}
                >
                  COD ৳ {formatTk(codAmount)} — courier collects
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: '8px' }}>
            <b style={{fontSize:'16px'}}>Total Bill: ৳ {formatTk(totalBill)}</b>
            {dueAmount > 0 && (
              isCourierSale ? (
                <b style={{color: '#b45309', fontSize: '14px'}}>COD: ৳ {formatTk(dueAmount)}</b>
              ) : (
                <b style={{color: 'red', fontSize: '14px'}}>Due: ৳ {formatTk(dueAmount)}</b>
              )
            )}
            {returnAmount > 0 && (
              <b style={{color: 'green', fontSize: '14px'}}>Return: ৳ {formatTk(returnAmount)}</b>
            )}
            {dueAmount === 0 && returnAmount === 0 && (
              <b style={{color: '#64748b', fontSize: '14px'}}>Fully Paid</b>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button onClick={() => { updateTabData(emptyTab()); setDeliveryPayerWarn(false); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: '#ffeded', color: 'red', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>CANCEL SALE</button>
          <button onClick={showInvoicePreview} className="btn-confirm" style={{ flex: 2, padding: '12px', fontSize: '13px' }}>REVIEW & CONFIRM</button>
        </div>
      </div>

      {previewInvoice && (
        <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ background:'#fff', padding:'30px', borderRadius:'25px', width:'450px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', position:'relative', maxHeight:'95vh', display: 'flex', flexDirection: 'column' }}>
            <button onClick={()=>setPreviewInvoice(null)} disabled={loading} style={{ position:'absolute', top:'15px', right:'15px', border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor: loading ? 'not-allowed' : 'pointer', fontWeight:'bold', zIndex: 10, opacity: loading ? 0.5 : 1 }}>X</button>
            
            <div id="printable-invoice" style={{ overflowY: 'auto', flex: 1, paddingRight: '5px', background: 'white' }}>
              <div style={{ textAlign:'center', marginBottom:'15px' }}>
                <h1 style={{ margin:0, color:'#4a00e0', fontSize:'22px' }}>PB KIZUNA COMPANY LIMITED</h1>
                <p style={{ margin:'4px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, <br/>Aftabnagar, Dhaka-1212</p>
                <p style={{ margin:0, fontSize:'11px' }}>Mobile: 01884-711844</p>
                <div style={{ borderBottom:'1px dashed #000', margin:'12px 0' }}></div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>SALES INVOICE</h4>
              </div>
              <div style={{ display:'flex', gap:'10px', fontSize:'12px', marginBottom:'12px' }}>
                <div style={{ flex:1 }}>
                  <p style={{ margin: '2px 0' }}><b>Sold By:</b> {previewInvoice.soldBy}</p>
                  <p style={{ margin: '2px 0' }}><b>Date:</b> {previewInvoice.dateTime}</p>
                  <p style={{ margin: '2px 0' }}><b>Invoice ID:</b> #{previewInvoice.invoiceId}</p>
                </div>
                <div style={{ flex:1, border:'1px solid #ddd', borderRadius:'8px', padding:'8px 10px', background:'#f8f9ff' }}>
                  <p style={{ margin: '0 0 4px 0', fontWeight:'bold', color:'#4a00e0' }}>Bill To</p>
                  <p style={{ margin: '2px 0' }}>{previewInvoice.name || 'Walk-in Customer'}</p>
                  {[previewInvoice.phone, previewInvoice.phone2].filter(Boolean).length > 0 && (
                    <p style={{ margin: '2px 0' }}>{[previewInvoice.phone, previewInvoice.phone2].filter(Boolean).join(', ')}</p>
                  )}
                  {previewInvoice.address && <p style={{ margin: '2px 0' }}>{previewInvoice.address}</p>}
                </div>
              </div>
              <div style={{ borderBottom:'1px dashed #000', margin:'8px 0 12px' }}></div>
              <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #eee' }}>
                    <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                    <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                    <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {previewInvoice.items.map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{padding:'5px 0'}}>
                        <b>{it.name}</b>
                        {it.itemDiscAmount > 0 && (
                          <div style={{fontSize:'10px', color:'#e74c3c'}}>
                            Disc ({it.itemDiscountText}): -৳ {formatTk(it.itemDiscAmount)}
                          </div>
                        )}
                      </td>
                      <td align='center'>{it.qty}</td>
                      <td align='right'>{formatTk(it.finalItemTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ borderTop:'1px solid #eee', marginTop:'10px', paddingTop:'8px', textAlign:'right', fontSize:'12px' }}>
                <p style={{ margin: '3px 0' }}>Sub Total: ৳ {formatTk(previewInvoice.subtotal)}</p>
                {previewInvoice.itemDiscountTotal > 0 && <p style={{ margin: '3px 0' }}>Item Discounts: -৳ {formatTk(previewInvoice.itemDiscountTotal)}</p>}
                {previewInvoice.overallDiscountVal > 0 && (
                  <p style={{ margin: '3px 0' }}>
                    Overall Discount ({previewInvoice.overallDiscountType === 'percent' ? `${previewInvoice.overallDiscountVal}%` : `৳`}): -৳ {formatTk(previewInvoice.overallDiscountAmount)}
                  </p>
                )}
                {previewInvoice.totalDiscount > 0 && (
                  <p style={{ margin: '3px 0', fontWeight: 'bold', color: '#e74c3c' }}>
                    Total Discount: -৳ {formatTk(previewInvoice.totalDiscount)}
                  </p>
                )}
                {previewInvoice.deliveryCharge > 0 && (
                  previewInvoice.deliveryPaidBy === 'shop' ? (
                    <p style={{ margin: '3px 0', color: '#c0392b' }}>
                      Delivery Charge: ৳ {formatTk(previewInvoice.deliveryCharge)} (paid by {previewInvoice.deliveryPaidByName})
                    </p>
                  ) : (
                    <p style={{ margin: '3px 0' }}>
                      Delivery Charge: +৳ {formatTk(previewInvoice.deliveryCharge)}
                    </p>
                  )
                )}
                <h3 style={{ margin:'6px 0', fontSize: '14px' }}>Grand Total: ৳ {formatTk(previewInvoice.totalBill)}</h3>
                <p style={{ margin: '3px 0' }}>{previewInvoice.deliveryMethod === 'courier' ? 'Advance Paid' : 'Paid'}{previewInvoice.paid > 0 ? ` (${previewInvoice.paidMethod || 'Cash'})` : ''}: ৳ {formatTk(previewInvoice.paid)}</p>
                {previewInvoice.dueAmount > 0 ? (
                  previewInvoice.deliveryMethod === 'courier' ? (
                    <p style={{ margin: '3px 0', color: '#b45309', fontWeight: 'bold' }}>Cash on Delivery: ৳ {formatTk(previewInvoice.dueAmount)}</p>
                  ) : (
                    <p style={{ margin: '3px 0', color: '#e74c3c', fontWeight: 'bold' }}>Due: ৳ {formatTk(previewInvoice.dueAmount)}</p>
                  )
                ) : (
                  <p style={{ margin: '3px 0' }}>Return: ৳ {formatTk(previewInvoice.returnAmount)}</p>
                )}
              </div>
              <p style={{ textAlign:'center', marginTop:'15px', fontSize:'11px', fontWeight:'bold' }}>Thank you, come again!</p>
            </div>

            {/* ---- বাটনগুলো এখন loading অবস্থায় disable থাকবে, তাই দ্রুত দুইবার ক্লিক করলেও
                     দ্বিতীয় ক্লিক কার্যকর হবে না — ডুপ্লিকেট সেল তৈরি হওয়া বন্ধ হয়ে যাবে ---- */}
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginTop:'15px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
              <div style={{ display:'flex', gap:'8px' }}>
                <button
                  onClick={()=>setPreviewInvoice(null)}
                  disabled={loading}
                  style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #ddd', cursor: loading ? 'not-allowed' : 'pointer', fontSize:'12px', opacity: loading ? 0.5 : 1 }}
                >
                  Edit Order
                </button>
                <button
                  onClick={()=>handleFinalProcess(false)}
                  disabled={loading}
                  style={{ flex:1, padding:'10px', borderRadius:'10px', background:'#eee', border:'none', cursor: loading ? 'not-allowed' : 'pointer', fontSize:'12px', opacity: loading ? 0.5 : 1 }}
                >
                  {loading ? 'Processing...' : 'Confirm (No Print)'}
                </button>
              </div>
              <button
                onClick={()=>handleFinalProcess(true)}
                disabled={loading}
                className="btn-confirm"
                style={{ padding:'10px', fontSize:'13px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Processing... Please Wait' : 'CONFIRM & PRINT RECEIPT 🖨️'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;